/**
 * Ro.ro mega menu controller (desktop only).
 *
 * The CSS already opens a panel on `:hover` / `:focus-within` of its nav item, so the menu
 * degrades gracefully with JS disabled. This script layers on the three things CSS cannot do:
 *
 *   1. A close DELAY. The panel starts at the bottom edge of the sticky nav, so the cursor
 *      has to cross the nav's own vertical padding to reach it. CSS :hover survives that
 *      because the panel is a DOM child of the <li>, but a fast diagonal move toward a card
 *      can clip outside the item's box on the way. A ~170ms close delay (plus the invisible
 *      bridge drawn by .roro-mega::before) makes that traversal reliable without leaving a
 *      real gap that would kill hover.
 *   2. Correct `aria-expanded` on the trigger.
 *   3. Escape to close, with focus returned to the trigger, and click-outside to close.
 *
 * Only one panel is ever open at a time.
 */
(function () {
  var OPEN_DELAY = 60;
  var CLOSE_DELAY = 170;
  // Matches the breakpoint at which .roro-navbar__left becomes visible. Below it the whole
  // desktop tree is display:none and the drawer accordion takes over, so we stay out.
  var DESKTOP_QUERY = '(min-width: 1024px)';

  function initNav(nav) {
    if (!nav || nav.dataset.roroMegaReady === 'true') return;

    var items = Array.prototype.slice.call(nav.querySelectorAll('[data-roro-mega-item]'));
    if (!items.length) return;

    nav.dataset.roroMegaReady = 'true';

    var desktop = window.matchMedia(DESKTOP_QUERY);
    var openTimer = null;
    var closeTimer = null;
    var openItem = null;

    function isDesktop() {
      return desktop.matches;
    }

    function clearTimers() {
      if (openTimer) {
        clearTimeout(openTimer);
        openTimer = null;
      }
      if (closeTimer) {
        clearTimeout(closeTimer);
        closeTimer = null;
      }
    }

    function setState(item, expanded) {
      var trigger = item.querySelector('[data-roro-mega-trigger]');
      item.classList.toggle('is-open', expanded);
      if (trigger) trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }

    function closeAll() {
      if (!openItem) return;
      setState(openItem, false);
      openItem = null;
    }

    function open(item) {
      if (openItem === item) return;
      closeAll();
      setState(item, true);
      openItem = item;
    }

    function scheduleOpen(item) {
      if (!isDesktop()) return;
      clearTimers();
      // Already-open panels swap instantly: waiting again would feel sticky when sliding
      // sideways along the nav.
      if (openItem) {
        open(item);
        return;
      }
      openTimer = setTimeout(function () {
        openTimer = null;
        open(item);
      }, OPEN_DELAY);
    }

    function scheduleClose() {
      clearTimers();
      closeTimer = setTimeout(function () {
        closeTimer = null;
        closeAll();
      }, CLOSE_DELAY);
    }

    items.forEach(function (item) {
      item.addEventListener('mouseenter', function () {
        scheduleOpen(item);
      });

      item.addEventListener('mouseleave', function () {
        scheduleClose();
      });

      // Keyboard / assistive tech: opening on focus means hover is never the only route in.
      item.addEventListener('focusin', function () {
        if (!isDesktop()) return;
        clearTimers();
        open(item);
      });

      item.addEventListener('focusout', function (event) {
        if (item.contains(event.relatedTarget)) return;
        scheduleClose();
      });
    });

    // Leaving the header entirely (e.g. sideways to the logo, or straight down into the page)
    // closes without waiting for a per-item mouseleave that may never fire. Bound on the whole
    // nav, not the link list: the panels are DOM children of their <li>, so moving down into a
    // panel is never a "leave" at any level above it.
    (nav.closest('.roro-navbar') || nav).addEventListener('mouseleave', scheduleClose);

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape' || !openItem) return;
      var trigger = openItem.querySelector('[data-roro-mega-trigger]');
      clearTimers();
      closeAll();
      if (trigger) trigger.focus();
    });

    document.addEventListener('click', function (event) {
      if (!openItem || openItem.contains(event.target)) return;
      clearTimers();
      closeAll();
    });

    // Panels are inert below the breakpoint; drop any open state when crossing it.
    var onBreakpointChange = function () {
      if (!isDesktop()) {
        clearTimers();
        closeAll();
      }
    };
    if (typeof desktop.addEventListener === 'function') {
      desktop.addEventListener('change', onBreakpointChange);
    } else if (typeof desktop.addListener === 'function') {
      desktop.addListener(onBreakpointChange);
    }
  }

  function initAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    Array.prototype.forEach.call(scope.querySelectorAll('[data-roro-mega-nav]'), initNav);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  // Theme editor re-renders the section without a page load.
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
